import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Eye } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { formatViews } from '../utils/formatViews';
import { carPath } from '../utils/carUrl';
import AdUnit from './AdUnit';

const BetweenAds = ({ cars, onViewDetails }) => {
  const { t } = useTranslation();
  const [ads, setAds] = useState([]);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/api/ads?position=between&active=true`);
        setAds(response.data || []);
      } catch (error) {
        console.error('Reklama yuklashda xatolik:', error);
        setAds([]);
      }
    };

    fetchAds();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('uz-UZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAdPositions = () => {
    const positions = [];
    const taken = new Set();

    ads.forEach((ad) => {
      const settings = ad.betweenSettings || {
        frequency: 3,
        startPosition: 1,
        maxPerPage: 5
      };

      const { frequency, startPosition, maxPerPage } = settings;
      let placed = 0;

      for (let i = startPosition - 1; i < cars.length && placed < maxPerPage; i += frequency) {
        if (taken.has(i)) continue;
        taken.add(i);
        positions.push({ position: i, ad });
        placed += 1;
      }
    });

    return positions;
  };

  const adPositions = getAdPositions();
  const elements = [];

  cars.forEach((car, index) => {
    elements.push({
      type: 'car',
      data: car,
      index
    });

    const adForPosition = adPositions.find((ap) => ap.position === index);
    if (adForPosition) {
      elements.push({
        type: 'ad',
        data: adForPosition.ad,
        index
      });
    }
  });

  const openCar = (car, e) => {
    if (e) {
      e.preventDefault();
    }
    if (onViewDetails) {
      onViewDetails(car);
    } else {
      window.location.href = carPath(car);
    }
  };

  return (
    <>
      {elements.map((element) => {
        if (element.type === 'car') {
          const car = element.data;
          const href = carPath(car);
          return (
            <Link
              key={car._id}
              to={href}
              className="car-entry"
              onClick={(e) => openCar(car, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCar(car);
                }
              }}
            >
              <div className="car-entry__media">
                {car.photos?.length > 0 ? (
                  <img
                    src={`${config.API_URL}/uploads/${car.photos[0]}`}
                    alt={`${car.licensePlate || 'Avto'} DTP`}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: '#1c2230' }} />
                )}
                <div className="car-entry__media-veil">{t('home.viewDetails')}</div>
                {car.photos?.length > 1 && (
                  <div className="car-entry__count">+{car.photos.length - 1}</div>
                )}
              </div>

              <div className="car-entry__body">
                <div className="car-entry__plate">{car.licensePlate}</div>
                <div className="car-entry__meta">
                  <span>
                    <Calendar size={14} />
                    {formatDate(car.createdAt)}
                  </span>
                  {car.location?.address && (
                    <span>
                      <MapPin size={14} />
                      {car.location.address}
                    </span>
                  )}
                </div>
                {car.description && (
                  <p className="car-entry__desc">{car.description}</p>
                )}
                <div className="car-entry__footer">
                  <span className="car-entry__action">{t('home.viewDetails')}</span>
                  <span className="views-count" title={t('home.views')}>
                    <Eye size={14} strokeWidth={2.25} />
                    {formatViews(car.views)}
                  </span>
                </div>
              </div>
            </Link>
          );
        }

        if (element.type === 'ad') {
          const ad = element.data;
          return (
            <div key={`ad-${ad._id}-${element.index}`} className="ad-inline-wrap">
              <AdUnit ad={ad} variant="feed" />
            </div>
          );
        }

        return null;
      })}
    </>
  );
};

export default BetweenAds;
